import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Popover } from "antd";
import { v4 as uuid } from "uuid";
import {
  EditorContext,
  EditorEvents,
  ReactNodeViewRenderer,
  useEditor
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import { CustomImage } from "./extensions";
import emojiData from "@emoji-mart/data";
import EmojiPicker from "@emoji-mart/react";
import {
  getListActivities,
  getUsersOptions
} from "Store/Activities/Activities.selector";
import { IUserData } from "Data/interfaces/User/IUserData";
import { useTimeout } from "Context/TimeoutContext";
import { useFeatureFlag } from "Hooks/useFeatureFlag";
import {
  attachmentsImagesAccepts,
  attachmentsImagesNotAccepts,
  ErrorImgType,
  maxAttachmentsUploadSize,
  sortActivities
} from "Utils/ActivitiesUtils";
import { loadImage } from "Utils/MultidomailUtils";
import Button from "Components/UI/Button";
import { Icon } from "Components/UI";
import SpinCustom from "Components/UI/SpinCustom";
import UploadImg from "Components/UI/UploadImg";
import LimitsInfo from "Components/UI/LimitsInfo";
import EditorComponent from "./components/Editor";
import userSuggestion from "./userSuggestion";
import activitySuggestion from "./activitySuggestion";
import { MentionUserDynamicDisplay } from "./components/MentionUserDisplay";
import { MentionActivityDynamicDisplay } from "./components/MentionActivityDisplay";
import { Container } from "./styles";

export interface IInputData {
  text: string;
}

interface IInput {
  disabled?: boolean;
  loading?: boolean;
  isUpdate?: boolean;
  defaultValue?: string;
  assignedUsers?: IUserData[];
  followersUsers?: IUserData[];
  onSubmit: (data: IInputData) => void;
  onActiveCallback?: (active: boolean) => void;
}

const Input: FC<IInput> = ({
  disabled,
  loading,
  isUpdate,
  defaultValue,
  assignedUsers,
  followersUsers,
  onSubmit,
  onActiveCallback,
}) => {
  const commentsAttachmentsFeatureFlag = useFeatureFlag('comments-attachments-feature');
  const commentsAttachmentsFeature = commentsAttachmentsFeatureFlag.enabled && commentsAttachmentsFeatureFlag.value === 'test';

  const { set, clear } = useTimeout();

  const usersOptions = useSelector(getUsersOptions);
  const listActivities = useSelector(getListActivities);
  const preventClose = useRef(false);
  const [active, setActive] = useState(false);
  const [visible, setVisible] = useState<boolean>();
  const [send, setSend] = useState(false);
  const [visibleUpload, setVisibleUpload] = useState(false);
  const [hasError, setHasError] = useState<ErrorImgType | null>(null);

  const listActivitiesFormat = useMemo(() => {
    if (listActivities?.activities && JSON.stringify(listActivities.activities) !== '{}') {
      const arrayList = Object.values(listActivities.activities);
      return arrayList.sort(sortActivities);
    }
    return [];
  }, [listActivities]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      CustomImage.configure({
        allowBase64: true,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention-user',
        },
        suggestion: userSuggestion(
          usersOptions,
          assignedUsers || [],
          followersUsers || []
        ),
      }).extend({
        name: 'mentionUser',
        addNodeView() {
          return ReactNodeViewRenderer(MentionUserDynamicDisplay);
        },
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention-activity',
        },
        suggestion: activitySuggestion(listActivitiesFormat),
      }).extend({
        name: 'mentionActivity',
        addNodeView() {
          return ReactNodeViewRenderer(MentionActivityDynamicDisplay);
        },
      }),
    ],
    content: defaultValue || '',
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'input',
      }
    },
    onBlur: () => {
      handleBlur(editor.commands.blur);
    },
    onFocus: (props: EditorEvents["focus"]) => {
      if (props.editor.isFocused) {
        setActive(true);
      }
    },
    onSelectionUpdate(props) {
      if (props.transaction.selection.content().openStart > 0) {
        togglePreventClose(true);
      }
    },
  });

  const providerValue = useMemo(() => ({ editor }), [editor]);

  const handleVisibleChange = (currentVisible: boolean) => {
    setVisible(currentVisible);
    if (!currentVisible) {
      editor.commands.focus();
    }
  };

  const handleBlur = (onBlur: () => void) => {
    onBlur();
    clear('Input-handleBlur');
    set('Input-handleBlur', () => {
      if (!visible && !visibleUpload) {
        if (!preventClose.current) {
          setActive(false);
          if (onActiveCallback) onActiveCallback(false);
        } else {
          editor.commands.focus();
        }
        togglePreventClose(false);
      }
    }, 200);
  };

  const togglePreventClose = (value: boolean) => {
    preventClose.current = value;
  };

  const openSelectEmoticon = () => {
    togglePreventClose(true);
    handleVisibleChange(!visible);
  };

  const handleSend = () => {
    togglePreventClose(true);
    clear('Input-handleSend');
    set('Input-handleSend', () => {
      setSend(true);
    }, 500);

    const contentJson = editor.getJSON();
    onSubmit({ text: JSON.stringify(contentJson) });
  };

  const handleEmoji = (e: any) => {
    editor.chain().focus().insertContent(e.native).run();
  };

  const handleClose = (clearEditor?: boolean) => {
    setSend(false);
    togglePreventClose(false);
    editor.commands.blur();
    if (clearEditor) {
      editor.commands.setContent('');
    }
    setActive(false);
    if (onActiveCallback) onActiveCallback(false);
  }

  const handleGetImageClick = () => {
    togglePreventClose(true);
    setVisibleUpload(true);
  };

  const handleCloseUpload = () => {
    clear('Input-handleCloseUpload');
    set('Input-handleCloseUpload', () => {
      togglePreventClose(false);
      setVisibleUpload(false);
      handleBlur(editor.commands.blur);
    }, 300);
  };

  const handleGetImage = (files: File | File[]) => {
    if (Array.isArray(files) && files.length > 0) {
      if (!files.every(file => attachmentsImagesAccepts.includes(file.type))) {
        handleCloseUpload();
        return setHasError('extension');
      }
      if (files.some(file => file.size > maxAttachmentsUploadSize)) {
        handleCloseUpload();
        return setHasError('size');
      }

      const imageId = uuid();

      editor.chain().focus().setImage({
        src: loadImage(files[0]),
        alt: 'Attachment',
        id: imageId,
        uploadStatus: 'uploading',
        progress: 0,
      }).run();

      // TODO: Implementar upload para o backend aqui
      // Exemplo de uso:
      // uploadImage(files[0], imageId, (progress) => {
      //   updateImageProgress(editor, imageId, progress);
      // }).then((response) => {
      //   completeImageUpload(editor, imageId, response.url);
      // }).catch(() => {
      //   markImageError(editor, imageId);
      // });
    }
    handleCloseUpload();
  };

  useEffect(() => {
    if (send && !loading) {
      handleClose(true);
    }
  }, [send, loading]);

  useEffect(() => {
    if (isUpdate) {
      setActive(true);
      editor.commands.focus();
    }
  }, [isUpdate, editor]);

  useEffect(() => {
    return () => {
      clear('Input-handleBlur');
      clear('Input-handleSend');
    }
  }, []);

  return (
    <Container active={active} disabled={disabled} isupdate={isUpdate}>
      <div className="editor">
        <EditorContext.Provider value={providerValue}>
          <EditorComponent
            editor={editor}
            togglePreventClose={togglePreventClose}
          />
        </EditorContext.Provider>
      </div>
      {active && (
        <div className="footer">
          <div className="footerLeft">
            <Popover
              placement="top"
              open={visible}
              onOpenChange={handleVisibleChange}
              content={
                <div>
                  <EmojiPicker
                    data={emojiData}
                    theme="light"
                    onEmojiSelect={handleEmoji}
                  />
                </div>
              }
            >
              <Icon
                icon="smile"
                customSize={18}
                className="iconSmile"
                onClick={openSelectEmoticon}
              />
            </Popover>
            {commentsAttachmentsFeature && (
              <UploadImg
                imagesAccepts={attachmentsImagesAccepts.join(', ')}
                imagesNotAccepts={attachmentsImagesNotAccepts}
                button={(
                  <Icon
                    icon="paperclip"
                    customSize={18}
                    className="iconPaperclip"
                    onClick={handleGetImageClick}
                  />
                )}
                onChange={handleGetImage}
                onCancel={handleCloseUpload}
              />
            )}
          </div>

          <div className="footerRight">
            {isUpdate && (
              <Button
                type="text"
                className="btnCancel"
                disabled={loading}
                onClick={loading ? undefined : () => handleClose(false)}
              >
                Cancelar
              </Button>
            )}
            <Button
              type="primary"
              className="btnSubmit"
              onClick={loading ? undefined : handleSend}
            >
              {loading && (
                <SpinCustom color="white" size={12} marginRigth={6} />
              )}
              {loading
                ? (isUpdate
                  ? 'Salvando...'
                  : 'Enviando...')
                : (isUpdate
                  ? 'Salvar'
                  : 'Enviar')
              }
            </Button>
          </div>
        </div>
      )}

      {!!hasError && (
        <LimitsInfo
          visible={!!hasError}
          isExtensionLimit={hasError === 'extension'}
          onCancel={() => setHasError(null)}
        />
      )}
    </Container>
  )
};

export default Input;
